import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { scrollToSection } from '../utils/scrollUtils';

const HeroContainer = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: url('/hacker/2.jpg') center/cover no-repeat;
  padding: 0 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.55); /* 55% black overlay for better text contrast */
    z-index: 1;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    height: auto;
    min-height: 100vh;
    padding: 2rem 0.5rem 120px 0.5rem; /* Extra bottom padding for FAB */
  }
`;

const HeroContent = styled.div`
  text-align: center;
  z-index: 2;
  position: relative;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

const HeroLogo = styled(motion.div)`
  margin-bottom: 2rem;
  
  img {
    height: 120px;
    width: auto;
    filter: drop-shadow(0 0 20px rgba(0, 255, 255, 0.8));
    animation: logoGlow 3s ease-in-out infinite;
  }
  
  @media (max-width: 600px) {
    img {
      height: 60px;
    }
    margin-bottom: 1rem;
  }
  
  @keyframes logoGlow {
    0%, 100% { filter: drop-shadow(0 0 20px rgba(0, 255, 255, 0.8)); }
    50% { filter: drop-shadow(0 0 30px rgba(0, 255, 255, 1)); }
  }
`;

const MainTitle = styled(motion.h1)`
  font-family: 'Orbitron', monospace;
  font-size: clamp(2rem, 8vw, 4.5rem);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  line-height: 1.1;
  word-break: break-word;
  overflow-wrap: break-word;
  @media (max-width: 600px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  .highlight {
    color: #00ffff;
    text-shadow: 0 0 20px #00ffff, 0 0 40px #00ffff;
  }
`;

const Subtitle = styled(motion.p)`
  font-family: 'Inter', sans-serif;
  font-size: clamp(1rem, 3vw, 1.2rem);
  font-weight: 300;
  color: #cccccc;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  @media (max-width: 600px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(45deg, #00ffff, #0080ff);
  border: none;
  padding: 1rem 3rem;
  font-family: 'Orbitron', monospace;
  font-size: 1.2rem;
  font-weight: 600;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  @media (max-width: 600px) {
    font-size: 1rem;
    padding: 0.7rem 1.5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 255, 255, 0.3);
  }
`;

const ScanningOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const ScanLine = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00ffff, transparent);
  box-shadow: 0 0 10px #00ffff;
`;

const DataStream = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0.1;
`;

const DataColumn = styled.div`
  position: absolute;
  top: -100%;
  width: 2px;
  height: 200%;
  background: linear-gradient(to bottom, transparent, #00ffff, transparent);
  animation: dataFlow 3s linear infinite;
  
  &:nth-child(1) { left: 10%; animation-delay: 0s; }
  &:nth-child(2) { left: 20%; animation-delay: 0.5s; }
  &:nth-child(3) { left: 30%; animation-delay: 1s; }
  &:nth-child(4) { left: 40%; animation-delay: 1.5s; }
  &:nth-child(5) { left: 50%; animation-delay: 2s; }
  &:nth-child(6) { left: 60%; animation-delay: 2.5s; }
  &:nth-child(7) { left: 70%; animation-delay: 0.3s; }
  &:nth-child(8) { left: 80%; animation-delay: 0.8s; }
  &:nth-child(9) { left: 90%; animation-delay: 1.3s; }
  
  @keyframes dataFlow {
    0% { top: -100%; }
    100% { top: 100%; }
  }
`;

const ThreatIndicators = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ThreatDot = styled(motion.div)`
  position: absolute;
  width: 8px;
  height: 8px;
  background: #ff0000;
  border-radius: 50%;
  box-shadow: 0 0 10px #ff0000;
`;

const Hero = ({ audioEnabled }) => {
  const scanLineRef = useRef(null);

  useEffect(() => {
    const scanLine = scanLineRef.current;
    if (scanLine) {
      const animate = () => {
        scanLine.style.top = '0px';
        scanLine.style.transition = 'none';
        
        setTimeout(() => {
          scanLine.style.top = '100vh';
          scanLine.style.transition = 'top 3s linear';
        }, 100);
      };
      
      animate();
      const interval = setInterval(animate, 4000);
      return () => clearInterval(interval);
    }
  }, []);

  const threatPositions = [
    { x: 15, y: 25 },
    { x: 85, y: 35 },
    { x: 45, y: 75 },
    { x: 75, y: 15 },
    { x: 25, y: 65 },
  ];

  return (
    <HeroContainer>
      <DataStream>
        {[...Array(9)].map((_, i) => (
          <DataColumn key={i} />
        ))}
      </DataStream>
      
      <ThreatIndicators>
        {threatPositions.map((pos, i) => (
          <ThreatDot
            key={i}
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          />
        ))}
      </ThreatIndicators>

      <ScanningOverlay>
        <ScanLine ref={scanLineRef} />
      </ScanningOverlay>

      <HeroContent>
        <HeroLogo
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <img src="/logo/Watch Dogs Logo 2 White.png" alt="Watchdogs Cyber Specialists" />
        </HeroLogo>
        
        <MainTitle
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          WATCHDOGS
          <br />
          <span className="highlight">CYBER</span>
          <br />
          SPECIALISTS
        </MainTitle>
        
        <Subtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
        >
          Elite Cybersecurity Solutions for the Modern Threat Landscape
        </Subtitle>
        
        <CTAButton
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection('contact')}
        >
          Activate Cyber Defence
        </CTAButton>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero; 